import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ChannelType } from "@/types/inbox";

export type InboxVariant = "page" | "peek";
export type ChannelFilterValue = ChannelType | "all";

interface InboxUIState {
	variant: InboxVariant;
	selectedId: string | null;
	setSelectedId: (id: string | null) => void;
	channelFilter: ChannelFilterValue;
	setChannelFilter: (channel: ChannelFilterValue) => void;
	search: string;
	setSearch: (search: string) => void;
	contextPanelOpen: boolean;
	setContextPanelOpen: (open: boolean) => void;
}

const InboxContext = createContext<InboxUIState | null>(null);

export function useInboxUI(): InboxUIState {
	const context = useContext(InboxContext);
	if (!context) throw new Error("useInboxUI must be used within an InboxProvider");
	return context;
}

/**
 * UI-only state for an inbox surface. Server data lives in react-query.
 * The full page mirrors the selected conversation to the URL (?c=...) for
 * refresh/deep-linking; the quick-peek drawer keeps selection local.
 */
export const InboxProvider: React.FC<{ variant: InboxVariant; children: React.ReactNode }> = ({
	variant,
	children,
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [localSelectedId, setLocalSelectedId] = useState<string | null>(null);
	const [channelFilter, setChannelFilter] = useState<ChannelFilterValue>("all");
	const [search, setSearch] = useState("");
	// Details panel starts open on desktop, closed on mobile (where it's a Sheet).
	const [contextPanelOpen, setContextPanelOpen] = useState(
		() => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
	);

	const selectedId = variant === "page" ? searchParams.get("c") : localSelectedId;

	const setSelectedId = useCallback(
		(id: string | null) => {
			if (variant === "page") {
				setSearchParams(
					(previous) => {
						const next = new URLSearchParams(previous);
						if (id) next.set("c", id);
						else next.delete("c");
						return next;
					},
					{ replace: true }
				);
			} else {
				setLocalSelectedId(id);
			}
		},
		[variant, setSearchParams]
	);

	const value = useMemo(
		() => ({
			variant,
			selectedId,
			setSelectedId,
			channelFilter,
			setChannelFilter,
			search,
			setSearch,
			contextPanelOpen,
			setContextPanelOpen,
		}),
		[variant, selectedId, setSelectedId, channelFilter, search, contextPanelOpen]
	);

	return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
};
